import { db } from './db';

const EMERGENCY_KEY = 'inspectionEmergencyDraft';

/** Optional live snapshot provider registered by the Inspections page. */
let snapshotProvider = null;

/**
 * Register a function that returns the current in-progress inspection draft
 * (serializable JSON only — no File objects). Used before forced logout.
 */
export function registerInspectionDraftSnapshotProvider(fn) {
  snapshotProvider = typeof fn === 'function' ? fn : null;
  return () => {
    if (snapshotProvider === fn) snapshotProvider = null;
  };
}

export async function captureInspectionDraftSnapshot() {
  if (!snapshotProvider) return null;
  try {
    const payload = snapshotProvider();
    if (!payload?.userId || !payload?.projectId || !payload?.templateId) return null;
    await saveInspectionEmergencyDraft(payload);
    return payload;
  } catch (err) {
    console.warn('[inspectionEmergencyDraft] capture failed:', err);
    return null;
  }
}

/**
 * Persist a user-scoped emergency draft. Survives forced session expiry so work
 * is not lost; cleared on intentional logout / after successful restore.
 * Does not weaken auth — data stays local and is keyed by userId.
 */
export async function saveInspectionEmergencyDraft({
  userId,
  projectId,
  templateId,
  draft_data,
  savedAt = new Date().toISOString()
}) {
  if (!userId || !projectId || !templateId || !draft_data) return;
  await db.referenceData.put({
    key: EMERGENCY_KEY,
    scope: String(userId),
    data: {
      userId: String(userId),
      projectId: String(projectId),
      templateId: String(templateId),
      draft_data,
      savedAt
    },
    updated_at: Date.now()
  });
}

export async function getInspectionEmergencyDraft(userId) {
  if (!userId) return null;
  try {
    const row = await db.referenceData.get([EMERGENCY_KEY, String(userId)]);
    return row?.data || null;
  } catch (err) {
    console.warn('[inspectionEmergencyDraft] get failed:', err);
    return null;
  }
}

export async function clearInspectionEmergencyDraft(userId) {
  try {
    if (userId) {
      await db.referenceData.delete([EMERGENCY_KEY, String(userId)]);
      return;
    }
    await db.referenceData.where('key').equals(EMERGENCY_KEY).delete();
  } catch (err) {
    console.warn('[inspectionEmergencyDraft] clear failed:', err);
  }
}

/** On login, drop other users' emergency drafts from this device (shared-device safety). */
export async function clearOtherUsersInspectionEmergencyDrafts(currentUserId) {
  if (!currentUserId) {
    await clearInspectionEmergencyDraft();
    return;
  }
  try {
    await db.referenceData
      .where('key')
      .equals(EMERGENCY_KEY)
      .filter((row) => String(row.scope) !== String(currentUserId))
      .delete();
  } catch (err) {
    console.warn('[inspectionEmergencyDraft] clearOtherUsers failed:', err);
  }
}

/**
 * Strip non-serializable File objects from itemAnswers for IndexedDB / offline queue / API.
 * @param {object} itemAnswers
 * @param {{ includePreviews?: boolean }} [options]
 *   includePreviews: only for local emergency recovery. Never send data-URL previews to the API
 *   (they bloat DB and duplicate files already stored via file_path).
 */
export function serializeItemAnswersForDraft(itemAnswers = {}, options = {}) {
  const { includePreviews = false } = options;
  const out = {};
  for (const [itemId, answer] of Object.entries(itemAnswers || {})) {
    if (!answer || typeof answer !== 'object') {
      out[itemId] = answer;
      continue;
    }
    const attachments = answer?.reportDetails?.attachments;
    if (!Array.isArray(attachments)) {
      out[itemId] = answer;
      continue;
    }
    out[itemId] = {
      ...answer,
      reportDetails: {
        ...answer.reportDetails,
        attachments: attachments
          .map((att) => {
            if (!att || typeof att !== 'object') return null;
            const file_path = att.file_path || null;
            const preview =
              includePreviews &&
              typeof att.preview === 'string' &&
              att.preview.startsWith('data:')
                ? att.preview
                : null;
            if (!file_path && !preview && !(att.file instanceof File)) return null;
            // Skip unsaved File blobs without a preview/path — cannot restore after reload
            if (att.file instanceof File && !file_path && !preview) return null;
            return {
              name: att.name || att.file_name || 'attachment',
              file_name: att.file_name || att.name || 'attachment',
              file_path,
              file_size: att.file_size || att.size || null,
              mime_type: att.mime_type || att.file_type || att.type || null,
              file_type: att.mime_type || att.file_type || att.type || null,
              photo_note: att.photo_note ?? '',
              ...(preview ? { preview } : {})
            };
          })
          .filter(Boolean)
      }
    };
  }
  return out;
}
